/**
 * AppShell Layout
 * 
 * Main authenticated layout wrapping all /dashboard/* routes.
 * Structure: Sidebar | TopBar + <Outlet />
 * 
 * - Desktop: persistent collapsible sidebar
 * - Mobile (<md): sidebar hidden, opened via Sheet
 * - Auth guard: redirects to /sign-in if not authenticated
 */

import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/auth';
import { isClerkConfigured } from '@/config';
import { useOnboardingStatus } from '@/onboarding';
import { isMandateUiEnabled } from '@/mandate/enabled';
import { useActiveMandate } from '@/mandate/api';
import { hasSeenMandateWizard, markMandateWizardSeen } from '@/mandate/wizardSeen';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Sidebar as MobileSidebar } from './Sidebar';
import { cn } from '@/lib/utils';
import { LoadingScreen as BrandedLoadingScreen } from '@/components/brand/LoadingScreen';
import { initUserAnalytics } from '@/lib/analytics';

// ---------------------------------------------------------------------------
// Loading screen
// ---------------------------------------------------------------------------

const LoadingScreen: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <BrandedLoadingScreen />
  </div>
);

// ---------------------------------------------------------------------------
// Mandate-first entry redirect
// ---------------------------------------------------------------------------

/**
 * Sends a user with no active mandate to the mandate wizard, and records in
 * this browser that it happened so the redirect never fires again. Mounted
 * only when the redirect is actually due, so the flag is written exactly
 * when the user is sent to the wizard.
 */
const MandateWizardEntry: React.FC = () => {
  useEffect(() => {
    markMandateWizardSeen();
  }, []);
  return <Navigate to="/dashboard/mandate/new" replace />;
};

// ---------------------------------------------------------------------------
// AppShell
// ---------------------------------------------------------------------------

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

export const AppShell: React.FC = () => {
  const { isLoaded, isSignedIn, id, email, tenantId, role } = useCurrentUser();
  const location = useLocation();
  // 2026-08-30: the ONLY thing that ever routed a user to
  // /dashboard/onboarding was the sign-up flow itself (CustomSignUp /
  // DevModeAuth navigating there right after signup). A user with a real,
  // signed-in session but a tenant that never completed onboarding -- e.g.
  // they closed the tab mid-wizard, or (the case that surfaced this) a
  // fresh tenant got auto-provisioned server-side while they already had
  // an open tab pointed at some other /dashboard/* route -- had no path
  // back to the wizard at all; every other page just rendered normally.
  // `isSkipped` (set by the wizard's own "Explore on my own" confirm flow)
  // is the real escape hatch, not silently bypassing this redirect.
  const onboardingStatus = useOnboardingStatus();

  // Mandate-first entry (2026-09-22 WORKFLOWS.md correction, narrowed by the
  // 2026-10-06 owner decision): while the mandate UI is on (always in mock/dev
  // mode; ff_mandate_v1 in production, see mandate/enabled.ts) a new user with
  // no signed mandate is sent to the mandate wizard once, on arrival at
  // /dashboard. Every other route stays reachable without a mandate; pages
  // that need one show an inline message instead (see NoMandateNotice).
  // `enabled` skips the request entirely when the mandate UI is off, so this
  // never fires a query against an endpoint that may not exist on a real
  // backend yet.
  const mandateUiOn = isMandateUiEnabled();
  const activeMandateQuery = useActiveMandate(mandateUiOn);

  // Sidebar collapse state — persisted to localStorage
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      // Storage unavailable: fall back to the default layout.
      return false;
    }
  });

  // Mobile sheet state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sheet on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !id) {
      return;
    }

    initUserAnalytics(id, {
      email,
      tenant_id: tenantId,
      role,
    });
  }, [isLoaded, isSignedIn, id, email, tenantId, role]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // Storage unavailable: the toggle still works for this session.
      }
      return next;
    });
  };

  // ---- Auth guard ----
  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (!isSignedIn && isClerkConfigured()) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // ---- Onboarding guard ----
  // Fail open on a loading/errored status fetch -- a transient backend
  // hiccup here must never trap a signed-in user who can't reach any page
  // at all, that's a worse failure mode than occasionally missing the
  // redirect.
  //
  // 2026-08-30: also fail open when the current required step IS
  // create_strategy ("Start a Conversation") and the destination is
  // Research. Its checklist button only navigates here -- it can't mark
  // itself complete on click the way create_account/verify_email do, since
  // completion only fires once a real conversation launches a workflow
  // (ResearchInterview.tsx's recordMilestone('create_strategy')). Without
  // this exemption, clicking that button is a dead end: this same guard
  // fires again the instant /dashboard/research renders (still incomplete)
  // and bounces the user straight back to /dashboard/onboarding before
  // ResearchInterview ever gets a chance to mount -- an infinite loop that
  // makes Research completely unreachable. Live-caught while recording a
  // full landing-to-completed-workflow walkthrough.
  const onResearchRouteToFinishOnboarding =
    onboardingStatus.currentStep === 'create_strategy' &&
    location.pathname.startsWith('/dashboard/research');

  if (mandateUiOn) {
    // ---- Mandate-first entry guard (replaces the onboarding guard below
    // only while the mandate UI is on) ----
    // Defer rendering (not decide the redirect) while on /dashboard/onboarding
    // AND the mandate query is still loading, so a fresh signup does not
    // flash the checklist before the one-time redirect is decided.
    if (location.pathname.startsWith('/dashboard/onboarding') && activeMandateQuery.isLoading) {
      return <LoadingScreen />;
    }

    // Fail open exactly like the onboarding guard: a loading or errored
    // fetch must never trap a signed-in user.
    const noActiveMandateYet =
      !activeMandateQuery.isLoading &&
      !activeMandateQuery.isError &&
      activeMandateQuery.data?.mandate == null;
    // Only the dashboard root, and only once per browser (see wizardSeen.ts).
    // Every other /dashboard/* route renders for a user with no mandate.
    const onDashboardRoot = /^\/dashboard\/?$/.test(location.pathname);
    if (noActiveMandateYet && onDashboardRoot && !hasSeenMandateWizard()) {
      return <MandateWizardEntry />;
    }
  } else if (
    // ---- Onboarding guard (original, untouched) ----
    !onboardingStatus.loading &&
    !onboardingStatus.error &&
    !onboardingStatus.isComplete &&
    !onboardingStatus.isSkipped &&
    !onResearchRouteToFinishOnboarding &&
    location.pathname !== '/dashboard/onboarding'
  ) {
    return <Navigate to="/dashboard/onboarding" replace />;
  }

  return (
      <TooltipProvider>
        <div className="flex h-screen overflow-hidden bg-background text-foreground">
          {/* Desktop sidebar */}
          <div className="hidden md:block">
            <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} />
          </div>

          {/* Mobile sidebar via Sheet */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-[240px] p-0" showCloseButton={false}>
              <MobileSidebar collapsed={false} onToggle={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Main content area */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <TopBar onMobileMenuToggle={() => setMobileOpen(true)} />

            <main className={cn(
              'flex-1 min-h-0 overflow-y-auto',
              'p-4 md:p-6',
            )}>
              <Outlet />
            </main>
          </div>
        </div>
      </TooltipProvider>
  );
};

export default AppShell;
